<template>
    <div>
        <div class="flex flex-col mb-7 gap-15">
            <div class=" flex items-center gap-15">
                <CallToAction :id="name"
                              variant="SecondaryButton"
                              class="min-w-200"
                              :required="required || undefined"
                              :aria-invalid="!!errorMessage"
                              :aria-describedby="errorMessage ? `${slugify(name)}-error` : undefined"
                              @click.prevent="onClick">
                    {{ $translate("Sponsorformular.ChooseFileButtonText") }}
                    <CIcon v-if="props.required"
                           name="required-mark"
                           class="my-auto mr-12 w-10 h-10 text-pink-100"/>
                </CallToAction>
                <div class="flex flex-wrap">
                    <div v-for="file in files"
                         :key="file.name"
                         class="flex items-center px-10 text-left hover:scale-105 cursor-pointer w-180 font-fauli">
                        <div class="flex flex-col items-start"
                             :class="{ 'text-red-100': file.size > 20000000 }">
                            {{ file.name }}
                            <span class="text-[10px] opacity-50"> ({{ formatFileSize(file.size) }})</span>
                        </div>
                        <button class="relative -right-2 bottom-2 px-2 ml-2 text-pink-100"
                                type="button"
                                :aria-label="`${$translate('Accessibility.RemoveFile')}: ${file.name}`"
                                @click="removeFile(file)">
                            X
                        </button>
                    </div>
                </div>
                <span v-if="files.length === 0"
                      id="no-files"
                      class="text-center font-fauli">{{
                          $translate("Sponsorformular.NoFiles") }}</span>
            </div>
        </div>
        <span v-if="filesHasBeenOverwritten"
              class="font-fauli">
            {{ $translate("Forms.FilesHasBeenOverwritten") }}
            <br>
        </span>
        <span v-if="totalMb > 20"
              class="text-red-100 font-fauli">
            {{ $translate("Sponsorformular.ToBigOfAFilePackage") }}
        </span>
        <span v-if="fileExtensionError"
              class="text-center text-red-100 opacity-50 font-fauli">{{
                  $translate("Sponsorformular.NotSupportedFile") }}: {{ fileExtensionError }}</span>
        <label :id="name"
               :for="name"></label>
        <Field :name="name">
            <input ref="inputRef"
                   :name="name"
                   type="file"
                   :accept="allowedFilesExtensions.join(', ')"
                   :multiple="multiple"
                   onclick="this.value=null"
                   class="hidden"
                   @change="onFileUpload">
        </Field>

        <InputErrorMessage v-if="errorMessage"
                           :id="`${slugify(name)}-error`"
                           class="mb-18 font-fauli"
                           :name="name"/>
    </div>
</template>

<script lang="ts" setup>
import { Field, useField } from 'vee-validate';
import { computed, ref } from 'vue';
import contentApi from '../content/api/contentApi';
import { formsPayloadLogging } from '@/project/basket/basket.service';
import CallToAction from '../navigation/call-to-action/CallToAction.vue';
import bus from '@/core/bus';
import { UploadFileError } from '@/project/forms/forms-services/forms.service';
import InputErrorMessage from './InputErrorMessage.vue';
import { slugify } from '../../core/forms/formsUtils';

type Props = {
    multiple: boolean;
    name: string;
    required: boolean;
}

const props = defineProps<Props>();

const inputRef = ref<HTMLInputElement | null>(null);
const files = ref<File[]>([]);
const allowedFilesExtensions = ref<string[]>(['.jpg']);
const fileExtensionError = ref<string | null>(null);
const filesHasBeenOverwritten = ref<boolean | null>(false);

contentApi.settings.then((settings) => {
    allowedFilesExtensions.value = settings.acceptedFileExtensions;
});


const { value, errorMessage, validate } = useField<File[]>(
    props.name,
    { required: props.required ? true : false },
    { validateOnValueUpdate: true },
);

const onClick = () => {
    if (!inputRef.value) {
        return;
    }

    inputRef.value?.click();
};

const totalMb = computed(() => {
    const size = files.value.reduce((acc, current) => acc + current.size, 0) / 1000 / 1000;
    if (size > 20) {
        bus.emit(UploadFileError, true);
    } else {
        bus.emit(UploadFileError, false);
    }
    return size;
});

const formatFileSize = (size: number) => {
    const mb = size / 1000 / 1000;
    const rounded = Math.round(mb);
    return rounded > 0 ? `${rounded} MB` : '< 1 MB';
};

const onFileUpload = async(e: Event) => {
    const target = e.target as HTMLInputElement;
    let text = '';

    fileExtensionError.value = null; // clear when uploading new files

    if (target && target.files) {
        for (let index = 0; index < target.files.length; index++) {
            const file = target.files[index];
            text += 'File: ' + file.name + ' size: ' + file.size + ' ';
            const lastDotIndex = file.name.lastIndexOf('.');
            const fileExtension = file.name.substring(lastDotIndex).toLocaleLowerCase();


            if (fileTypeIsAllowed(fileExtension)) {
                files.value.push(file);
                bus.emit(UploadFileError, false);
            } else {
                bus.emit(UploadFileError, true);
                fileExtensionError.value = fileExtension;
            }
        }
    }

    await formsPayloadLogging('On file upload (InputFile.vue)', text);

    bus.emit('TempFileCounterInput', files.value.length);

    value.value = files.value;
    validate();
};

const removeFile = async(file: File) => {
    files.value = files.value.filter((f) => f.name !== file.name);

    value.value = files.value;

    if (files.value.length <= 0) {
        filesHasBeenOverwritten.value = false;
    }

    let loggingText = 'Files: ';
    files.value.forEach(file => {
        loggingText += 'filename: ' + file.name + ', file size: ' + file.size + ' ';
    });

    bus.emit('TempFileCounterInput', files.value.length);
    await formsPayloadLogging('Removed file (InputFile.vue)', loggingText);
    validate();
};

const fileTypeIsAllowed = (extension: string) => {
    return allowedFilesExtensions.value.includes(extension);
};

</script>
<style scoped>
#no-files {
    color: rgba(0, 0, 0, 0.7); /* 70% opacity black for better contrast */
    font-weight: 500; /* Medium weight for better readability */
}
</style>
